import { Bullet, Circle, Label, Series, color, p50 } from "@amcharts/amcharts5";
import { AxisRendererY, ColumnSeries, SmoothedXLineSeries, ValueAxis, XYChart, XYCursor } from "@amcharts/amcharts5/xy";

import { LegendData } from "@/classes/BaseChart";
import ChartWithLegend from "@/classes/BaseChart/ChartWithLegend";
import ChartWithTooltip from "@/classes/BaseChart/ChartWithTooltip";
import StackedColumnChart from "@/classes/StackedColumnChart";
import { PLAN_MODULE } from "@/constants";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { defaultColorSet } from "@/settings/colors";
import { chartFontSettings } from "@/settings/widgets";
import { ChartDataRecord } from "@/types/charts";

const baseCreateTooltip = (
  ChartWithTooltip.prototype as unknown as {
    createTooltip: (series: Series, createParams: (paramsTooltip: Label) => Record<string, unknown>[]) => void;
  }
).createTooltip;

const baseShowLegend = (
  ChartWithLegend.prototype as unknown as {
    showLegend: (inputNames: string[], legendData: [], sortByNames?: boolean, isClickable?: boolean) => void;
  }
).showLegend;

const STACK_TOTAL_FIELD = "__stackTotal";
const LINE_COLOR_INDEX = 1;

export default class StackedColumnLineChart extends StackedColumnChart {
  private getStackMetrics(): string[] {
    const metrics = this.settings.displayMetrics;
    return (Array.isArray(metrics) ? metrics : [metrics]).filter(Boolean) as string[];
  }

  protected createSeries(name: string, index: number, fieldName: string) {
    const paletteLength = this.palette.length;
    const adjusted = (index >= LINE_COLOR_INDEX ? index + 1 : index) % paletteLength;
    return super.createSeries(name, adjusted, fieldName);
  }

  protected chartDataFields(): string[] {
    return [this.settings.xAxis, ...this.getStackMetrics(), this.settings.lineMetric].filter(Boolean) as string[];
  }

  protected createTooltip = (
    series: Series,
    _createParams: (paramsTooltip: Label) => Record<string, unknown>[]
  ): void => {
    const createParams = (target: Label): Record<string, unknown>[] => {
      const row = (target?.dataItem?.dataContext ?? {}) as ChartDataRecord;

      return [
        { key: this.settings.xAxis, value: row.name },
        ...this.metrics.map((metric) => ({ key: metric, value: row[metric] })),
      ];
    };

    baseCreateTooltip.call(this, series, createParams);
  };

  protected getDataLabelText(fieldName: string): string {
    return this.inputsDictionary?.displayMetrics?.find((item) => item.value === fieldName)?.title ?? fieldName;
  }

  protected getDataLabelMetricKey(fieldName: string): string {
    return fieldName;
  }

  protected createOverlaySeries(): string[] {
    const lineMetric = this.settings.lineMetric;
    if (!lineMetric) return [];

    ((this.chart as XYChart).series.values.slice(0, this.metrics.length) as ColumnSeries[]).forEach((columnSeries) => {
      columnSeries.set("tooltip", undefined);
      columnSeries.columns.template.remove("tooltipText");
    });

    this.yAxis.set("extraMax", 0.1);

    const lineYAxis = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        min: 0,
        strictMinMax: true,
        extraMax: 0.1,
        renderer: AxisRendererY.new(this.root, { opposite: true }),
        numberFormat: this.getCompactAxesMetricFormatter(lineMetric, this.data),
      })
    );

    const lineRenderer = lineYAxis.get("renderer") as AxisRendererY;
    lineRenderer.grid.template.setAll({ forceHidden: true });
    lineRenderer.labels.template.setAll({ ...chartFontSettings.label });

    const colorIndex = LINE_COLOR_INDEX;
    const series: SmoothedXLineSeries = (this.chart as XYChart).series.push(
      SmoothedXLineSeries.new(this.root, {
        name: lineMetric,
        xAxis: this.xAxis,
        yAxis: lineYAxis,
        valueYField: lineMetric,
        categoryXField: "name",
        stroke: this.getSeriesColor(colorIndex),
        fill: this.getSeriesColor(colorIndex),
      })
    );

    series.strokes.template.setAll({ strokeWidth: 2 });

    series.bullets.push(() =>
      Bullet.new(this.root, {
        sprite: Circle.new(this.root, { radius: 4, fill: series.get("fill") }),
      })
    );

    if (this.module === PLAN_MODULE && Boolean(this.settings.showDataLabels)) {
      const lineFormatter = this.formatters?.[lineMetric];
      const formatterConfig = this.getDataLabelsFormatterConfig();
      const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

      series.bullets.push((_root, _series, dataItem) => {
        const label = Label.new(this.root, {
          text: "",
          populateText: false,
          centerX: p50,
          dy: -30,
          fontSize: labelFontSize,
        });

        label.adapters.add("text", () => {
          const value = dataItem?.get("valueY");
          if (typeof value !== "number") return "";
          return lineFormatter
            ? lineFormatter(value, formatterConfig)
            : this.root.numberFormatter.format(value, formatterConfig);
        });

        label.adapters.add("visible", () => {
          const value = dataItem?.get("valueY");
          return typeof value === "number" ? value !== 0 : false;
        });

        return Bullet.new(this.root, { locationY: 1, sprite: label });
      });
    }

    baseCreateTooltip.call(this, series, (target: Label) => {
      const row = (target?.dataItem?.dataContext ?? {}) as ChartDataRecord;
      return [
        { key: this.settings.xAxis, value: row.name },
        { key: lineMetric, value: row[lineMetric] },
      ];
    });

    series.data.setAll(this.data);

    const stackTooltipCarrier = (this.chart as XYChart).series.push(
      ColumnSeries.new(this.root, {
        xAxis: this.xAxis,
        yAxis: this.yAxis,
        valueYField: STACK_TOTAL_FIELD,
        categoryXField: "name",
        clustered: false,
        fill: color(defaultColorSet[0]),
      })
    );

    stackTooltipCarrier.columns.template.setAll({ fillOpacity: 0, strokeOpacity: 0 });
    this.createTooltip(stackTooltipCarrier, () => []);
    stackTooltipCarrier.data.setAll(this.data);

    const cursor = (this.chart as XYChart).set("cursor", XYCursor.new(this.root, { behavior: "none" }));
    cursor.lineY.set("visible", false);

    this.xAxis.get("tooltip")?.set("forceHidden", true);

    return [lineMetric];
  }

  protected showLegend = (): void => {
    const paletteLength = this.palette.length;
    const lineMetric = this.settings.lineMetric;

    const entries = this.metrics.map((metric, index) => ({
      name: this.getDataLabelText(metric),
      colorIndex: (index >= LINE_COLOR_INDEX ? index + 1 : index) % paletteLength,
    }));

    if (lineMetric) {
      entries.push({ name: this.getDataLabelText(lineMetric), colorIndex: LINE_COLOR_INDEX });
    }

    const legendSeries = entries.map((entry) => {
      const proxy = (this.chart as XYChart).series.push(
        ColumnSeries.new(this.root, {
          name: entry.name,
          xAxis: this.xAxis,
          yAxis: this.yAxis,
          clustered: false,
          fill: this.getSeriesColor(entry.colorIndex),
          stroke: this.getSeriesColor(entry.colorIndex),
        })
      );
      proxy.columns.template.set("forceHidden", true);
      return proxy;
    });

    baseShowLegend.call(
      this,
      entries.map((entry) => entry.name),
      legendSeries as LegendData,
      false
    );
  };

  protected mapData(data: []): ChartDataRecord[] {
    const xAxis = this.settings.xAxis;
    const metrics = this.getStackMetrics();
    const lineMetric = this.settings.lineMetric;

    this.metrics = metrics;

    const rows = new Map<string, ChartDataRecord>();

    (data as ChartDataRecord[]).forEach((record) => {
      const bucket = String(record[xAxis] ?? "");
      if (!bucket) return;

      const row = rows.get(bucket) ?? { name: bucket };
      metrics.forEach((metric) => {
        row[metric] = (Number(row[metric]) || 0) + (Number(record[metric]) || 0);
      });
      if (lineMetric) {
        row[lineMetric] = (Number(row[lineMetric]) || 0) + (Number(record[lineMetric]) || 0);
      }
      rows.set(bucket, row);
    });

    let mapped = Array.from(rows.values());
    mapped.forEach((row) => {
      row[STACK_TOTAL_FIELD] = metrics.reduce((acc, metric) => acc + (Number(row[metric]) || 0), 0);
    });
    mapped = this.showItems(mapped, "name", undefined);
    this.names = mapped.map((row) => row.name as string);

    return mapped;
  }
}
